import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { archiveSegment, listSegments } from "@/lib/emailApi";
import { describeRule } from "@/lib/segments";
import { formatAgo, formatInt } from "@/lib/format";
import { BrandMark } from "@/components/BrandMark";
import { Empty, PageHeader } from "@/components/PageHeader";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TD, TH, THead, TR } from "@/components/ui/table";
import { useTags } from "../contacts/hooks";

/** Saved audiences for broadcasts, with their last counts. */
export function SegmentsPage() {
  const q = useQuery({ queryKey: ["segments"], queryFn: () => listSegments() });
  const tags = useTags(true);
  const qc = useQueryClient();
  const toast = useToast();
  const archive = useMutation({
    mutationFn: (id: number) => archiveSegment(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["segments"] });
      toast("Segment archived");
    },
  });
  const tagName = (id: number) => tags.data?.tags.find((t) => t.id === id)?.name ?? `#${id}`;
  const rows = q.data ?? [];
  return (
    <div className="mx-auto max-w-[1200px]">
      <PageHeader
        title="Segments"
        sub="who a broadcast goes to"
        actions={
          <>
            <Button size="sm" asChild>
              <Link to="/email/segments/$segmentId" params={{ segmentId: "new" }}>
                <Plus /> New segment
              </Link>
            </Button>
          </>
        }
      >
        <p className="text-sm text-[var(--color-muted-foreground)]">
          A segment is a set of rules, not a fixed list, so it's always current. Opt-Out and other “no marketing” tags, bounced and unsubscribed addresses, and archived contacts are left out of every send automatically.
        </p>
      </PageHeader>
      <Card className="overflow-x-auto">
        {q.isLoading && <Skeleton className="m-3 h-32" />}
        {q.error && <p className="px-4 py-3 text-sm text-[var(--color-destructive)]">Couldn't load segments: {q.error.message}</p>}
        {!q.isLoading && !q.error && rows.length === 0 && (
          <Empty title="No segments yet.">Make one from scratch, or open a saved search on the Contacts page and choose “Use for an email”.</Empty>
        )}
        {rows.length > 0 && (
          <Table>
            <THead>
              <tr>
                <TH>Segment</TH>
                <TH>Brand</TH>
                <TH>Rules</TH>
                <TH className="text-right">People</TH>
                <TH className="text-right">Would get email</TH>
                <TH>Counted</TH>
                <TH />
              </tr>
            </THead>
            <tbody>
              {rows.map((s) => (
                <TR key={s.id}>
                  <TD className="max-w-[16rem]">
                    <Link to="/email/segments/$segmentId" params={{ segmentId: String(s.id) }} className="block truncate font-medium text-[var(--color-primary)] hover:underline">
                      {s.name}
                    </Link>
                    {s.description && <div className="truncate text-xs text-[var(--color-muted-foreground)]">{s.description}</div>}
                  </TD>
                  <TD>{s.brand_id ? <BrandMark id={s.brand_id} /> : "—"}</TD>
                  <TD className="max-w-[24rem] text-xs text-[var(--color-muted-foreground)]">
                    <div className="line-clamp-2">
                      {s.definition
                        ? s.definition.rules.map((r) => describeRule(r, tagName)).join(s.definition.match === "any" ? "; or " : "; and ") || "everyone"
                        : "Open it to see the rules"}
                    </div>
                  </TD>
                  <TD className="num text-right">{s.last_count != null ? formatInt(s.last_count) : "—"}</TD>
                  <TD className="num text-right">{s.last_mailable != null ? formatInt(s.last_mailable) : "—"}</TD>
                  <TD className="whitespace-nowrap text-xs">{s.last_counted_at ? formatAgo(s.last_counted_at) : "—"}</TD>
                  <TD className="whitespace-nowrap text-right">
                    <Button size="sm" variant="ghost" asChild>
                      <Link to="/email/broadcasts/$broadcastId" params={{ broadcastId: "new" }} search={{ segment: s.id, brand: s.brand_id ?? undefined }}>
                        New broadcast
                      </Link>
                    </Button>
                    <Button size="sm" variant="ghost" className="text-[var(--color-muted-foreground)]" onClick={() => window.confirm(`Archive "${s.name}"? Past broadcasts keep their report.`) && archive.mutate(s.id)}>
                      Archive
                    </Button>
                  </TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
